import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '~/apis/cn03FormApi';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationsListResponse,
  type UnreadCountResponse,
} from '~/apis/notificationsApi';
import { getAxiosStatus } from '~/lib/auth/logout';

export const NOTIFICATIONS_UNREAD_COUNT_KEY = ['notifications', 'unread-count'] as const;
export const NOTIFICATIONS_LIST_KEY = ['notifications', 'list'] as const;

const UNREAD_POLL_MS = 60_000;
const DEFAULT_PAGE_SIZE = 20;

export function useNotificationUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY,
    queryFn: getUnreadCount,
    enabled,
    refetchInterval: enabled ? UNREAD_POLL_MS : false,
    select: (data) => data.unreadCount,
  });
}

type UseNotificationsListOptions = {
  enabled: boolean;
  unreadOnly: boolean;
};

export function useNotificationsList({ enabled, unreadOnly }: UseNotificationsListOptions) {
  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_LIST_KEY, { unreadOnly }],
    queryFn: ({ pageParam }) =>
      listNotifications({
        page: pageParam,
        pageSize: DEFAULT_PAGE_SIZE,
        unreadOnly,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, pageSize, total } = lastPage;
      if (page * pageSize >= total) return undefined;
      return page + 1;
    },
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: markNotificationRead,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY });
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_LIST_KEY });

      const previousUnread = queryClient.getQueryData<UnreadCountResponse>(
        NOTIFICATIONS_UNREAD_COUNT_KEY
      );
      const previousLists = queryClient.getQueriesData<{
        pages: NotificationsListResponse[];
        pageParams: number[];
      }>({ queryKey: NOTIFICATIONS_LIST_KEY });

      if (previousUnread && previousUnread.unreadCount > 0) {
        queryClient.setQueryData<UnreadCountResponse>(NOTIFICATIONS_UNREAD_COUNT_KEY, {
          unreadCount: previousUnread.unreadCount - 1,
        });
      }

      const now = new Date().toISOString();
      queryClient.setQueriesData<{
        pages: NotificationsListResponse[];
        pageParams: number[];
      }>({ queryKey: NOTIFICATIONS_LIST_KEY }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((item) =>
              item.id === id ? { ...item, readAt: item.readAt ?? now } : item
            ),
            unreadCount: Math.max(
              0,
              page.unreadCount -
                (page.items.some((i) => i.id === id && i.readAt == null) ? 1 : 0)
            ),
          })),
        };
      });

      return { previousUnread, previousLists };
    },
    onError: (err, _id, context) => {
      if (context?.previousUnread) {
        queryClient.setQueryData(NOTIFICATIONS_UNREAD_COUNT_KEY, context.previousUnread);
      }
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      const status = getAxiosStatus(err);
      const message =
        status === 404
          ? 'Notification not found.'
          : getApiErrorMessage(err, 'Could not mark notification as read.');
      enqueueSnackbar(message, { variant: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY });
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_LIST_KEY });

      const previousUnread = queryClient.getQueryData<UnreadCountResponse>(
        NOTIFICATIONS_UNREAD_COUNT_KEY
      );
      const previousLists = queryClient.getQueriesData<{
        pages: NotificationsListResponse[];
        pageParams: number[];
      }>({ queryKey: NOTIFICATIONS_LIST_KEY });

      queryClient.setQueryData<UnreadCountResponse>(NOTIFICATIONS_UNREAD_COUNT_KEY, {
        unreadCount: 0,
      });

      const now = new Date().toISOString();
      queryClient.setQueriesData<{
        pages: NotificationsListResponse[];
        pageParams: number[];
      }>({ queryKey: NOTIFICATIONS_LIST_KEY }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            unreadCount: 0,
            items: page.items.map((item) => ({
              ...item,
              readAt: item.readAt ?? now,
            })),
          })),
        };
      });

      return { previousUnread, previousLists };
    },
    onError: (err, _vars, context) => {
      if (context?.previousUnread) {
        queryClient.setQueryData(NOTIFICATIONS_UNREAD_COUNT_KEY, context.previousUnread);
      }
      if (context?.previousLists) {
        for (const [key, data] of context.previousLists) {
          queryClient.setQueryData(key, data);
        }
      }
      enqueueSnackbar(getApiErrorMessage(err, 'Could not mark all notifications as read.'), {
        variant: 'error',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY });
    },
  });
}
